import { useState, useEffect, useMemo, useCallback } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, cloneDeep } from 'lodash';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import Loader from '../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { BombiixSidePanel } from '../../Components/BombiixSidePanel';
import { NewSelectMultiSelect } from '../../Components/PIM/common/NewSelectMultiSelect';
import { NewInput } from '../../Components/PIM/common/NewInput';
import { AddIcon, DeleteIcon } from '../../bombiix-icon';
import {
  deleteAdditionalVariableFromSystemMasters,
  getAdditionalVariableInUseStatus,
  getAdditionalVariablesDetails,
  getBrandDetails,
  getCategory1Details,
  getCategory2Details,
  getPCTypesDetails,
  saveAdditionalVariableToSystemMaster,
} from './Store/SystemMasters.action';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { toasterMsg } from '../../Components/Toaster/Toaster';

/** A master entity (brand, product type, category) offered as a scope option. */
interface MasterEntity {
  _id: string;
  name?: string;
  isActive?: boolean;
  type?: number;
}

/** One entry of a scope dropdown. */
interface ScopeOption {
  value: string;
  label?: string | undefined;
  disabled?: boolean;
}

/** An allowed option value belonging to an Additional Variable. */
interface VariableOption {
  _id?: string;
  name: string;
  isNew?: boolean;
}

/** One Additional Variable record, as held in the master list and edited in the side panel. */
interface AdditionalVariableRecord {
  _id: string;
  name: string;
  brand: string[];
  productType: string[];
  category1: string[];
  category2: string[];
  options: VariableOption[];
  isNew?: boolean;
  isActive?: boolean;
}

/** A draft name typed into an inline add row, before it is saved. */
interface DraftInput {
  id: string | number;
  value: string;
  error?: string | null | undefined;
}

/** Per-field validation messages, keyed by the field or row they belong to. */
type FieldErrors = Record<string | number, string | boolean | null>;

/** Draft names being edited in place, keyed by variable id. */
type EditedNames = Record<string, string>;

/** Scope field whose in-use ids lock the matching dropdown options against removal. */
type ScopeField = 'brand' | 'productType' | 'category1' | 'category2';

/** In-use scope ids a product depends on, grouped by scope field. */
type InUseScope = Record<ScopeField, string[]>;

/** What the in-use-status endpoint reports back for one variable. */
interface InUseStatus {
  variableInUse?: boolean;
  inUseOptionIds?: unknown[];
  inUseScope?: Partial<Record<ScopeField, unknown[]>>;
}

/** A scope value as stored: either a plain id or a populated document. */
type ScopeValue = string | { _id?: string; value?: string };

/** Dispatch as this screen uses it — every action here is a thunk resolving to its payload. */
type ThunkDispatch = <TResult = unknown>(action: unknown) => Promise<TResult>;

/** The redux slices this screen reads. */
interface SystemMastersRootState {
  SystemMastersDetails: {
    additionalVariablesList?: AdditionalVariableRecord[];
    brandList?: MasterEntity[];
    pcTypeList?: MasterEntity[];
    category1List?: MasterEntity[];
    category2List?: MasterEntity[];
  };
  UserDetails: { userInfo: { userId: string } };
  Users: { logInRights: unknown };
}

const TEMP_ADDITIONAL_VARIABLE: AdditionalVariableRecord = {
  name: '',
  brand: [],
  productType: [],
  category1: [],
  category2: [],
  options: [],
  isNew: true,
  isActive: true,
  _id: '',
};

const SCOPE_FIELDS: ScopeField[] = ['brand', 'productType', 'category1', 'category2'];

const emptyInUseScope = (): InUseScope => ({ brand: [], productType: [], category1: [], category2: [] });

const normaliseInUseScope = (scope: InUseStatus['inUseScope']): InUseScope => {
  const normalised = emptyInUseScope();
  SCOPE_FIELDS.forEach((field) => {
    const values = scope?.[field];
    normalised[field] = Array.isArray(values) ? values.map((id) => String(id)) : [];
  });
  return normalised;
};

/**
 * Additional Variables system master. Each record is a single renamable variable created by name,
 * then configured (via its settings icon) with a brand + product type + category1 + category2 scope
 * and a list of allowed option values. On a product, a matching variable renders as a dropdown of
 * these options.
 * @returns {JSX.Element}
 */
function AdditionalVariablesMaster() {
  const dispatch = useDispatch() as unknown as ThunkDispatch;
  const { t } = useTranslation(['additionalVariablesMaster', 'systemMaster']);

  const {
    SystemMastersDetails: { additionalVariablesList, brandList, pcTypeList, category1List, category2List },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((state: SystemMastersRootState) => state);

  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'samples', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'samples', 'delete');

  const loggedInUserId = userInfo.userId;

  const [variableData, setVariableData] = useState<AdditionalVariableRecord[]>([]);
  const [showVariables, setShowVariables] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string>('');
  const [toggleDelete, setToggleDelete] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<AdditionalVariableRecord | null>(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  /* Top-level variable-name add/rename state */
  const [newVariableInputs, setNewVariableInputs] = useState<DraftInput[]>([]);
  const [isSavingVariables, setIsSavingVariables] = useState(false);
  const [editedVariableNames, setEditedVariableNames] = useState<EditedNames>({});
  const [editedVariableErrors, setEditedVariableErrors] = useState<FieldErrors>({});

  /* Draft option inputs added within the sidebar */
  const [newOptionInputs, setNewOptionInputs] = useState<DraftInput[]>([]);
  const [optionErrors, setOptionErrors] = useState<FieldErrors>({});

  /* In-use guards: which variable ids are used by a product (locks list delete), which option ids of
     the open variable are in use (locks those option deletes), and which scope ids products depend on
     (locks those brand/product type/cat1/cat2 options against removal) */
  const [inUseVariableIds, setInUseVariableIds] = useState<string[]>([]);
  const [inUseOptionIds, setInUseOptionIds] = useState<string[]>([]);
  const [inUseScope, setInUseScope] = useState<InUseScope>(emptyInUseScope);

  useEffect(() => {
    setVariableData(additionalVariablesList || []);
  }, [additionalVariablesList]);

  /**
   * Resolve which of the given variables are in use by a product, so their list delete icons lock.
   * @param variables the variable records to check
   */
  const refreshInUseVariableIds = async (variables: AdditionalVariableRecord[]) => {
    const candidates = (variables || []).filter((variable: AdditionalVariableRecord) => variable?._id);
    try {
      const results = await Promise.all(
        candidates.map((variable: AdditionalVariableRecord) =>
          dispatch<InUseStatus>(getAdditionalVariableInUseStatus({ additionalVariableId: String(variable._id) })).then((status: InUseStatus) => ({
            id: String(variable._id),
            inUse: !!status?.variableInUse,
          })),
        ),
      );
      setInUseVariableIds(results.filter((entry) => entry.inUse).map((entry) => entry.id));
    } catch (error) {
      setInUseVariableIds([]);
    }
  };

  const fetchVariables = async () => {
    if (!showVariables) {
      setIsLoading(true);
      setShowVariables(true);
      const variables = await dispatch(getAdditionalVariablesDetails({ loggedInUserId }));
      await refreshInUseVariableIds((variables ?? []) as AdditionalVariableRecord[]);
      setIsLoading(false);
    } else {
      setShowVariables(false);
    }
  };

  const fetchDropdownData = useCallback(async () => {
    setSidebarLoading(true);
    await Promise.all([
      isEmpty(brandList) && dispatch(getBrandDetails({ loggedInUserId })),
      isEmpty(pcTypeList) && dispatch(getPCTypesDetails({ includeInactive: true }, true)),
      isEmpty(category1List) && dispatch(getCategory1Details({ loggedInUserId, categoryType: 1 })),
      isEmpty(category2List) && dispatch(getCategory2Details({ loggedInUserId, categoryType: 2 })),
    ]);
    setSidebarLoading(false);
  }, [dispatch, loggedInUserId, brandList, pcTypeList, category1List, category2List]);

  /**
   * Reduce a scope value to its plain id, whether it arrived as a string or a populated document.
   * @param item the stored scope value
   * @returns the id as a string
   */
  const extractId = (item: ScopeValue): string => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return String(item._id || item.value || item);
    return String(item);
  };

  /**
   * Normalise every scope array on a variable to plain id strings.
   * @param variable the variable as held in the list
   * @returns a clone whose scope arrays hold ids only
   */
  const normalizeVariableIds = (variable: AdditionalVariableRecord): AdditionalVariableRecord => {
    const normalized = cloneDeep(variable);
    const scopeFields: (keyof AdditionalVariableRecord)[] = ['brand', 'productType', 'category1', 'category2'];
    scopeFields.forEach((field) => {
      const values = normalized[field];
      if (Array.isArray(values)) {
        (normalized[field] as string[]) = (values as ScopeValue[]).map(extractId);
      }
    });
    return normalized;
  };

  const openSidebar = async (variable: AdditionalVariableRecord) => {
    const clonedVariable = normalizeVariableIds(variable);
    setSelectedVariable(clonedVariable);
    setShowSidebar(true);
    setErrors({});
    setNewOptionInputs([]);
    setOptionErrors({});
    setInUseOptionIds([]);
    setInUseScope(emptyInUseScope());
    await fetchDropdownData();

    /* Lock the option-delete icons and in-use scope options for what a product has already selected */
    if (clonedVariable?._id) {
      try {
        const status = await dispatch<InUseStatus>(getAdditionalVariableInUseStatus({ additionalVariableId: String(clonedVariable._id) }));
        setInUseOptionIds((status?.inUseOptionIds ?? []).map((id: unknown) => String(id)));
        setInUseScope(normaliseInUseScope(status?.inUseScope));
      } catch (error) {
        setInUseOptionIds([]);
        setInUseScope(emptyInUseScope());
      }
    }
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    setNewOptionInputs([]);
    setOptionErrors({});
    setInUseOptionIds([]);
    setInUseScope(emptyInUseScope());
  };

  /* Add a new top-level variable name input */
  const addNewVariableInput = async () => {
    if (!showVariables) {
      await fetchVariables();
    }
    setNewVariableInputs((prev) => [...prev, { id: Date.now(), value: '', error: null }]);
  };

  const updateVariableInput = (id: string | number, value: string) => {
    setNewVariableInputs((prev) =>
      prev.map((input: DraftInput): DraftInput => (input.id === id ? { ...input, value, error: value.trim() ? null : input.error } : input)),
    );
  };

  const removeVariableInput = (id: string | number) => {
    setNewVariableInputs((prev) => prev.filter((input) => input.id !== id));
  };

  const validateVariableInputs = () => {
    const existingNames = variableData.map((variable) => (variable.name || '').toLowerCase());
    let hasErrors = false;

    const updatedInputs = newVariableInputs.map((input) => {
      const value = input.value.trim();
      if (!value) {
        hasErrors = true;
        return { ...input, error: t('ADDITIONAL_VARIABLES_MASTER.NAME_REQUIRED') };
      }
      if (value.length > 50) {
        hasErrors = true;
        return { ...input, error: t('ADDITIONAL_VARIABLES_MASTER.NAME_TOO_LONG') };
      }
      const duplicateCount = newVariableInputs.filter((inp) => inp.value.trim().toLowerCase() === value.toLowerCase() && inp.id !== input.id).length;
      if (duplicateCount > 0 || existingNames.includes(value.toLowerCase())) {
        hasErrors = true;
        return { ...input, error: t('ADDITIONAL_VARIABLES_MASTER.DUPLICATE_NAME') };
      }
      return { ...input, error: null };
    });

    setNewVariableInputs(updatedInputs);
    return !hasErrors;
  };

  const validateEditedVariableNames = () => {
    const validationErrors: FieldErrors = {};
    for (const [variableId, newName] of Object.entries(editedVariableNames)) {
      const trimmed = newName.trim();
      if (!trimmed) {
        validationErrors[variableId] = t('ADDITIONAL_VARIABLES_MASTER.NAME_REQUIRED');
        continue;
      }
      if (trimmed.length > 50) {
        validationErrors[variableId] = t('ADDITIONAL_VARIABLES_MASTER.NAME_TOO_LONG');
      }
    }
    setEditedVariableErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const saveAllVariableNames = async () => {
    const hasEdits = Object.keys(editedVariableNames).length > 0;
    const hasNew = newVariableInputs.length > 0;
    if (!hasEdits && !hasNew) return;

    const editsValid = hasEdits ? validateEditedVariableNames() : true;
    const newValid = hasNew ? validateVariableInputs() : true;
    if (!editsValid || !newValid) return;

    setIsSavingVariables(true);
    try {
      for (const [variableId, newName] of Object.entries(editedVariableNames)) {
        const variable = variableData.find((existing) => existing._id === variableId);
        if (!variable) continue;
        const payload = {
          loggedInUserId,
          additionalVariable: { ...normalizeVariableIds(variable), name: newName.trim() },
        };
        await dispatch(saveAdditionalVariableToSystemMaster(payload, t));
      }

      for (const input of newVariableInputs) {
        const variableName = input.value.trim();
        if (variableName) {
          const payload = {
            loggedInUserId,
            additionalVariable: { ...TEMP_ADDITIONAL_VARIABLE, name: variableName },
          };
          await dispatch(saveAdditionalVariableToSystemMaster(payload, t));
        }
      }

      await dispatch(getAdditionalVariablesDetails({ loggedInUserId }));
      setEditedVariableNames({});
      setEditedVariableErrors({});
      setNewVariableInputs([]);
      toasterMsg(t('ADDITIONAL_VARIABLES_MASTER.SAVED_SUCCESSFULLY'), 'success');
    } catch (error) {
      toasterMsg(t('ADDITIONAL_VARIABLES_MASTER.SAVE_ERROR'), 'danger');
    } finally {
      setIsSavingVariables(false);
    }
  };

  const handleInlineVariableNameChange = (variableId: string, newName: string) => {
    const original = variableData.find((variable) => variable._id === variableId)?.name ?? '';
    setEditedVariableNames((prev) => {
      if (newName === original) {
        const { [variableId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [variableId]: newName };
    });
    setEditedVariableErrors((prev) => {
      const { [variableId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const brandOptions = useMemo(
    () =>
      brandList?.filter((brand: MasterEntity) => brand.isActive)?.map((brand: MasterEntity): ScopeOption => ({ value: brand._id, label: brand.name })) || [],
    [brandList],
  );

  const productTypeOptions = useMemo(
    () =>
      pcTypeList?.filter((pcType: MasterEntity) => pcType.isActive)?.map((pcType: MasterEntity): ScopeOption => ({ value: pcType._id, label: pcType.name })) ||
      [],
    [pcTypeList],
  );

  const category1Options = useMemo(
    () =>
      category1List
        ?.filter((category: MasterEntity) => category.isActive && category.type === 1)
        ?.map((category: MasterEntity): ScopeOption => ({ value: category._id, label: category.name })) || [],
    [category1List],
  );

  const category2Options = useMemo(
    () =>
      category2List
        ?.filter((category: MasterEntity) => category.isActive && category.type === 2)
        ?.map((category: MasterEntity): ScopeOption => ({ value: category._id, label: category.name })) || [],
    [category2List],
  );

  const applyInUseLock = (options: ScopeOption[], field: ScopeField): ScopeOption[] => {
    const lockedIds = inUseScope[field];
    if (!lockedIds.length) return options;
    return options.map((option) => (lockedIds.includes(String(option.value)) ? { ...option, disabled: true } : option));
  };

  const handleMultiSelectChange = (field: ScopeField, values: ScopeOption[] | null) => {
    const ids = values?.map((value: ScopeOption) => String(value.value)) || [];
    /* Re-add in-use scope ids so a locked value can never be dropped if the control emits a set
       without it. */
    const merged = Array.from(new Set([...ids, ...inUseScope[field]]));
    setSelectedVariable((prev) => (prev ? { ...prev, [field]: merged } : prev));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const getSelectedValues = (ids: string[] | undefined, options: ScopeOption[]): ScopeOption[] => {
    if (!ids?.length) return [];
    return options.filter((option: ScopeOption) => ids.includes(option.value));
  };

  /* Rename a saved option value within the selected variable */
  const handleOptionNameChange = (index: number, value: string) => {
    const updated = cloneDeep(selectedVariable);
    const option = updated?.options?.[index];
    if (updated && option) {
      option.name = value;
      setSelectedVariable(updated);
    }
    if (errors.options) {
      setErrors((prev) => ({ ...prev, options: null }));
    }
  };

  const removeOption = (index: number) => {
    setSelectedVariable((prev) =>
      prev
        ? {
            ...prev,
            options: prev.options.filter((_option: VariableOption, currentIndex: number) => currentIndex !== index),
          }
        : prev,
    );
  };

  const addNewOptionInput = () => {
    setNewOptionInputs((prev) => [...prev, { id: Date.now(), value: '' }]);
  };

  const updateOptionInput = (id: string | number, value: string) => {
    setNewOptionInputs((prev) => prev.map((input: DraftInput) => (input.id === id ? { ...input, value } : input)));
    if (optionErrors[id]) {
      setOptionErrors((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  const removeOptionInput = (id: string | number) => {
    setNewOptionInputs((prev) => prev.filter((input) => input.id !== id));
    if (optionErrors[id]) {
      setOptionErrors((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  const validateNewOptions = () => {
    const newErrors: FieldErrors = {};
    let hasErrors = false;
    const existingNames = selectedVariable?.options?.map((option) => (option.name || '').toLowerCase()) || [];

    newOptionInputs.forEach((input) => {
      const value = input.value.trim();
      if (!value) {
        /* Empty draft option rows are ignored on save (not required), so skip validating them */
        return;
      }
      if (value.length > 50) {
        newErrors[input.id] = t('ADDITIONAL_VARIABLES_MASTER.OPTION_NAME_TOO_LONG');
        hasErrors = true;
      } else if (existingNames.includes(value.toLowerCase())) {
        newErrors[input.id] = t('ADDITIONAL_VARIABLES_MASTER.OPTION_EXISTS');
        hasErrors = true;
      } else {
        const duplicateCount = newOptionInputs.filter((inp) => inp.id !== input.id && inp.value.trim().toLowerCase() === value.toLowerCase()).length;
        if (duplicateCount > 0) {
          newErrors[input.id] = t('ADDITIONAL_VARIABLES_MASTER.DUPLICATE_OPTION');
          hasErrors = true;
        }
      }
    });

    setOptionErrors(newErrors);
    return !hasErrors;
  };

  const mergeNewOptions = (variable: AdditionalVariableRecord): AdditionalVariableRecord => {
    const updated = cloneDeep(variable);
    const draftOptions: VariableOption[] = newOptionInputs
      .filter((input: DraftInput) => input.value.trim())
      .map((input: DraftInput): VariableOption => ({ name: input.value.trim() }));
    updated.options = [...(updated.options || []), ...draftOptions];
    return updated;
  };

  const validateForm = () => {
    const newErrors: FieldErrors = {};
    /* Required scope fields — the red border (invalid) conveys the error, no helper text is shown */
    if (!selectedVariable?.brand?.length) {
      newErrors.brand = true;
    }
    if (!selectedVariable?.productType?.length) {
      newErrors.productType = true;
    }
    if (!selectedVariable?.category1?.length) {
      newErrors.category1 = true;
    }
    if (!selectedVariable?.category2?.length) {
      newErrors.category2 = true;
    }

    const savedNames = selectedVariable?.options?.map((option) => (option.name ?? '').toLowerCase().trim()) ?? [];
    const duplicateName = savedNames.some((name, index) => name && savedNames.indexOf(name) !== index);
    if (duplicateName) {
      newErrors.options = t('ADDITIONAL_VARIABLES_MASTER.OPTION_EXISTS');
    }

    setErrors(newErrors);
    const optionsValid = validateNewOptions();
    return Object.keys(newErrors).length === 0 && optionsValid;
  };

  const saveVariable = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        loggedInUserId,
        additionalVariable: mergeNewOptions(selectedVariable as AdditionalVariableRecord),
      };
      await dispatch(saveAdditionalVariableToSystemMaster(payload, t));

      const updatedVariables = await dispatch<AdditionalVariableRecord[]>(getAdditionalVariablesDetails({ loggedInUserId }));
      const updatedVariable = updatedVariables?.find((variable: AdditionalVariableRecord) => variable._id === selectedVariable?._id);
      if (updatedVariable) {
        await openSidebar(updatedVariable);
      }
      toasterMsg(t('ADDITIONAL_VARIABLES_MASTER.SAVED_SUCCESSFULLY'), 'success');
      setNewOptionInputs([]);
      setOptionErrors({});
    } catch (error) {
      /* Duplicate name/scope errors are already surfaced by the action — don't double-toast a generic message */
      const isDuplicate = typeof error === 'object' && error !== null && 'isDuplicate' in error && Boolean((error as { isDuplicate?: unknown }).isDuplicate);
      if (!isDuplicate) {
        toasterMsg(t('ADDITIONAL_VARIABLES_MASTER.SAVE_ERROR'), 'danger');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setToggleDelete(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    await dispatch(deleteAdditionalVariableFromSystemMasters({ loggedInUserId, additionalVariableId: deleteId }, t));
    await dispatch(getAdditionalVariablesDetails({ loggedInUserId }));
    setEditedVariableNames((prev) => {
      const { [deleteId]: _removed, ...rest } = prev;
      return rest;
    });
    setIsLoading(false);
    setToggleDelete(false);
  };

  return (
    <>
      <Accordion className="mb-4 master-accordian additional-variables-master">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('ADDITIONAL_VARIABLES_MASTER.TITLE')}</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {MasterAddEditAccess && (
                <CustomTooltip tooltipText={t('ADDITIONAL_VARIABLES_MASTER.ADD_VARIABLE')}>
                  <button
                    className="btn btn-outline sm ml-auto mr-2 -0"
                    onClick={addNewVariableInput}
                    aria-label={t('ADDITIONAL_VARIABLES_MASTER.ADD_VARIABLE')}
                    disabled={isSavingVariables}
                    type="button"
                  >
                    <span className="icon-Add f-20" />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip tooltipText={t(showVariables ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}>
                <button
                  className="btn d-inline-flex p-0"
                  onClick={fetchVariables}
                  aria-label={t(showVariables ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}
                  type="button"
                >
                  <i className={`icon-new-down-arrow-icon ${showVariables ? 'icon-inverse' : ''} f-24`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showVariables && (
            <Card.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {variableData.map((variable) => (
                        <li key={variable._id}>
                          <div className="input-container flex-grow-1">
                            {MasterAddEditAccess ? (
                              <>
                                <input
                                  type="text"
                                  className={`px-0${!variable.isActive ? ' text-muted' : ''}`}
                                  value={editedVariableNames[variable._id] ?? variable.name}
                                  onChange={(e) => handleInlineVariableNameChange(variable._id, e.target.value)}
                                  maxLength={50}
                                  disabled={isSavingVariables}
                                />
                                {editedVariableErrors[variable._id] && <div className="invalid-feedback d-block">{editedVariableErrors[variable._id]}</div>}
                              </>
                            ) : (
                              <span className={!variable.isActive ? 'text-muted' : ''}>{variable.name}</span>
                            )}
                          </div>
                          <div className="input-action d-flex">
                            {MasterAddEditAccess && (
                              <CustomTooltip tooltipText={t('ADDITIONAL_VARIABLES_MASTER.CONFIGURE')}>
                                <button
                                  type="button"
                                  className="btn p-0 custom-action mr-0"
                                  onClick={() => openSidebar(variable)}
                                  aria-label={t('ADDITIONAL_VARIABLES_MASTER.CONFIGURE')}
                                >
                                  <span className="icon-settings f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                            {MasterDeleteAccess && (
                              <CustomTooltip
                                tooltipText={
                                  inUseVariableIds.includes(String(variable._id))
                                    ? t('ADDITIONAL_VARIABLES_MASTER.VARIABLE_IN_USE')
                                    : t('CUSTOM_ATTRIBUTES.DELETE')
                                }
                              >
                                <button
                                  type="button"
                                  className="btn custom-action mr-0"
                                  onClick={() => handleDelete(variable._id)}
                                  disabled={inUseVariableIds.includes(String(variable._id))}
                                  aria-label="delete"
                                >
                                  <span className="icon-delete f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </li>
                      ))}

                      {newVariableInputs.map((input, index) => (
                        <li key={input.id} className="adding-new-group">
                          <div className="input-container flex-grow-1">
                            <input
                              id={`additional-variable-input-${input.id}`}
                              type="text"
                              className="form-control px-0"
                              value={input.value}
                              onChange={(e) => updateVariableInput(input.id, e.target.value)}
                              placeholder={t('ADDITIONAL_VARIABLES_MASTER.ENTER_NAME')}
                              maxLength={50}
                              autoFocus={index === newVariableInputs.length - 1}
                            />
                            {input.error && <div className="invalid-feedback d-block">{input.error}</div>}
                          </div>
                          <div className="input-action d-flex">
                            <CustomTooltip tooltipText={t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}>
                              <button
                                type="button"
                                className="btn p-0 custom-action mr-0"
                                onClick={() => removeVariableInput(input.id)}
                                disabled={isSavingVariables}
                                aria-label={t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}
                              >
                                <span className="icon-delete f-24" />
                              </button>
                            </CustomTooltip>
                          </div>
                        </li>
                      ))}

                      {variableData.length === 0 && newVariableInputs.length === 0 && (
                        <li className="text-center text-muted">{t('ADDITIONAL_VARIABLES_MASTER.NO_VARIABLES')}</li>
                      )}
                    </ul>
                  </div>
                  {(newVariableInputs.length > 0 || Object.keys(editedVariableNames).length > 0) && (
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button type="button" className="btn btn-outline-none " onClick={saveAllVariableNames} disabled={isSavingVariables}>
                        {isSavingVariables ? <Spinner animation="border" size="sm" className="mr-2" /> : <span className="icon-Tick-1 f-26" />}
                      </button>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>

      {/* Settings side panel (master design): scope (brand/product type/cat1/cat2) + option values */}
      <BombiixSidePanel
        isOpen={showSidebar}
        title={selectedVariable?.name || ''}
        onClose={closeSidebar}
        onSave={saveVariable}
        saveLabel={t('ADDITIONAL_VARIABLES_MASTER.SAVE')}
        saveDisabled={isSaving}
        saveLoading={isSaving}
        className="additional-variables-side-panel"
      >
        {sidebarLoading ? (
          <div className="p-4">
            <Loader />
          </div>
        ) : (
          <>
            <div className="mb-3">
              <NewSelectMultiSelect
                label={t('ADDITIONAL_VARIABLES_MASTER.BRAND')}
                required
                isMulti
                options={applyInUseLock(brandOptions, 'brand')}
                isOptionDisabled={(option) => Boolean(option.disabled)}
                value={getSelectedValues(selectedVariable?.brand, brandOptions)}
                onChange={(selected) => handleMultiSelectChange('brand', selected as ScopeOption[] | null)}
                placeholder={t('ADDITIONAL_VARIABLES_MASTER.SELECT_BRAND')}
                selectAllLabel={t('ADDITIONAL_VARIABLES_MASTER.SELECT_ALL')}
                invalid={!!errors.brand}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>

            <div className="mb-3">
              <NewSelectMultiSelect
                label={t('ADDITIONAL_VARIABLES_MASTER.PRODUCT_TYPE')}
                required
                isMulti
                options={applyInUseLock(productTypeOptions, 'productType')}
                isOptionDisabled={(option) => Boolean(option.disabled)}
                value={getSelectedValues(selectedVariable?.productType, productTypeOptions)}
                onChange={(selected) => handleMultiSelectChange('productType', selected as ScopeOption[] | null)}
                placeholder={t('ADDITIONAL_VARIABLES_MASTER.SELECT_PRODUCT_TYPE')}
                selectAllLabel={t('ADDITIONAL_VARIABLES_MASTER.SELECT_ALL')}
                invalid={!!errors.productType}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>

            <div className="mb-3">
              <NewSelectMultiSelect
                label={t('ADDITIONAL_VARIABLES_MASTER.CATEGORY_1')}
                required
                isMulti
                options={applyInUseLock(category1Options, 'category1')}
                isOptionDisabled={(option) => Boolean(option.disabled)}
                value={getSelectedValues(selectedVariable?.category1, category1Options)}
                onChange={(selected) => handleMultiSelectChange('category1', selected as ScopeOption[] | null)}
                placeholder={t('ADDITIONAL_VARIABLES_MASTER.SELECT_CATEGORY')}
                selectAllLabel={t('ADDITIONAL_VARIABLES_MASTER.SELECT_ALL')}
                invalid={!!errors.category1}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>

            <div className="mb-3">
              <NewSelectMultiSelect
                label={t('ADDITIONAL_VARIABLES_MASTER.CATEGORY_2')}
                required
                isMulti
                options={applyInUseLock(category2Options, 'category2')}
                isOptionDisabled={(option) => Boolean(option.disabled)}
                value={getSelectedValues(selectedVariable?.category2, category2Options)}
                onChange={(selected) => handleMultiSelectChange('category2', selected as ScopeOption[] | null)}
                placeholder={t('ADDITIONAL_VARIABLES_MASTER.SELECT_CATEGORY')}
                selectAllLabel={t('ADDITIONAL_VARIABLES_MASTER.SELECT_ALL')}
                invalid={!!errors.category2}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>

            {/* Option values */}
            <div className="additional-variables-options">
              {errors.options && <div className="text-danger small mb-2">{errors.options}</div>}

              {selectedVariable?.options?.map((option, index) => {
                const optionInUse = inUseOptionIds.includes(String(option._id));
                return (
                  <div key={option._id || index} className="d-flex align-items-center mb-2 additional-variables-option-row">
                    <div className="flex-grow-1">
                      <NewInput value={option.name ?? ''} maxLength={50} onChange={(e) => handleOptionNameChange(index, e.target.value)} />
                    </div>
                    <CustomTooltip tooltipText={optionInUse ? t('ADDITIONAL_VARIABLES_MASTER.OPTION_IN_USE') : t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}>
                      <button
                        type="button"
                        className="bombiix-icon-container bombiix-icon-container--inactive"
                        onClick={() => removeOption(index)}
                        disabled={optionInUse}
                        aria-label={t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}
                      >
                        <DeleteIcon size={24} />
                      </button>
                    </CustomTooltip>
                  </div>
                );
              })}

              {newOptionInputs.map((input, index) => (
                <div key={input.id} className="d-flex align-items-center mb-2 additional-variables-option-row">
                  <div className="flex-grow-1">
                    <NewInput
                      value={input.value}
                      maxLength={50}
                      placeholder={t('ADDITIONAL_VARIABLES_MASTER.ENTER_OPTION_NAME')}
                      invalid={!!optionErrors[input.id]}
                      autoFocus={index === newOptionInputs.length - 1}
                      onChange={(e) => updateOptionInput(input.id, e.target.value)}
                    />
                    {optionErrors[input.id] && <span className="error-message d-block">{optionErrors[input.id]}</span>}
                  </div>
                  <CustomTooltip tooltipText={t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}>
                    <button
                      type="button"
                      className="bombiix-icon-container bombiix-icon-container--inactive"
                      onClick={() => removeOptionInput(input.id)}
                      aria-label={t('ADDITIONAL_VARIABLES_MASTER.REMOVE')}
                    >
                      <DeleteIcon size={24} />
                    </button>
                  </CustomTooltip>
                </div>
              ))}

              <div className="d-flex align-items-center justify-content-end gap-2 mt-3 additional-variables-add-row">
                <span>{t('ADDITIONAL_VARIABLES_MASTER.ADD_OPTION')}</span>
                <button
                  type="button"
                  className="bombiix-icon-container bombiix-icon-container--blue active"
                  onClick={addNewOptionInput}
                  aria-label={t('ADDITIONAL_VARIABLES_MASTER.ADD_OPTION')}
                >
                  <AddIcon size={24} />
                </button>
              </div>
            </div>
          </>
        )}
      </BombiixSidePanel>

      {toggleDelete && (
        <ConfirmationPopup
          onOpen={toggleDelete}
          onConfirm={confirmDelete}
          title={t('ADDITIONAL_VARIABLES_MASTER.DELETE_CONFIRMATION.TITLE')}
          message={t('ADDITIONAL_VARIABLES_MASTER.DELETE_CONFIRMATION.MESSAGE')}
          toggle={() => setToggleDelete(false)}
          loading={isLoading}
        />
      )}
    </>
  );
}

AdditionalVariablesMaster.propTypes = {};

export default AdditionalVariablesMaster;
